import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { joApi, joErr } from '@/services/processingJoApi';
import { DateRangeBar } from './DateRangeBar';
import { JoBadge, fmtDate, fmtN, routeLabel } from './JoBits';
import { exportTrace } from './exportTrace';

const STATUSES = [['', 'All'], ['OPEN', 'Open'], ['COMPLETED', 'Completed'], ['CLOSED', 'Closed']];
const HEAD = ['Job order', 'FG Name', 'Route', 'Status', 'Sent', 'In SFG', 'In progress', 'Lost', 'Most pieces now', 'Stuck at', 'PO · supplier'];

const Row = ({ o, onOpen }) => (
  <TableRow className="cursor-pointer hover:bg-[#F5EFE3]" onClick={() => onOpen(o.jo_id)} data-testid={`trace-row-${o.jo_id}`}>
    <TableCell><div className="font-mono text-sm font-medium">{o.jo_id}</div><div className="text-[11px] text-gray-500">{fmtDate(o.created_at)}</div></TableCell>
    <TableCell className="max-w-[200px]"><div className="truncate font-mono text-sm">{o.fg_name}</div><div className="truncate text-[11px] text-gray-500">{o.design_name}</div></TableCell>
    <TableCell className="whitespace-nowrap text-xs">{routeLabel(o.route)}</TableCell>
    <TableCell><JoBadge status={o.status === 'OPEN' ? 'IN_PROGRESS' : o.status} /></TableCell>
    {[['sent', o.sent_pcs, ''], ['sfg', o.done_pcs, 'text-emerald-700 font-semibold'], ['progress', o.in_progress_pcs, 'text-sky-700'], ['lost', o.lost_pcs, 'text-rose-700']].map(([k, n, c]) => (
      <TableCell key={k} className={`text-right tabular-nums ${n ? c : ''}`} data-testid={`trace-${k}-${o.jo_id}`}>{fmtN(n)}</TableCell>))}
    <TableCell className="whitespace-nowrap text-sm" data-testid={`trace-now-${o.jo_id}`}>{o.current ? `${o.current} · ${fmtN(o.current_pcs)}` : '—'}</TableCell>
    <TableCell className="text-xs text-amber-700" data-testid={`trace-stuck-${o.jo_id}`}>{o.stuck_at.join(', ') || '—'}</TableCell>
    <TableCell className="max-w-[200px] text-xs" data-testid={`trace-po-${o.jo_id}`}><div className="truncate font-mono">{o.po_numbers.join(', ') || 'No PO linked'}</div>
      <div className="truncate text-gray-500">{o.suppliers.join(', ')}</div></TableCell>
  </TableRow>
);

/** Job orders raised in a date range — where the pieces are, what's stuck or lost, and their sources; CSV = one row per stage. */
export const TraceReport = ({ q }) => {
  const navigate = useNavigate();
  const [range, setRange] = useState({ from: '', to: '' });
  const [status, setStatus] = useState('');
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    const t = setTimeout(() => joApi.traceReport({ q: q || undefined, status: status || undefined, date_from: range.from || undefined, date_to: range.to || undefined })
      .then(setData).catch((e) => toast.error(joErr(e, 'Could not load the report'))), 300);
    return () => clearTimeout(t);
  }, [q, status, range]);
  return (
    <div className="space-y-3" data-testid="jo-trace-report">
      <div className="flex flex-wrap items-center gap-3">
        <DateRangeBar from={range.from} to={range.to} onChange={setRange} testPrefix="trace-date" />
        <div className="flex rounded-md border bg-white p-0.5" data-testid="trace-status">
          {STATUSES.map(([v, l]) => <button key={l} type="button" onClick={() => setStatus(v)} data-testid={`trace-status-${l.toLowerCase()}`}
            className={`rounded px-3 py-1.5 text-sm transition-colors ${status === v ? 'bg-[#1A1F2E] text-white' : 'text-gray-600 hover:bg-gray-100'}`}>{l}</button>)}
        </div>
        <Button variant="outline" size="sm" className="ml-auto" onClick={() => exportTrace(data.rows)} disabled={!data?.rows.length} data-testid="trace-csv-btn"><Download className="mr-1 h-4 w-4" />CSV</Button>
      </div>
      {data && <p className="text-xs text-gray-500" data-testid="trace-range-note">Job orders raised {data.date_from} to {data.date_to} · {data.rows.length} orders{data.more ? ` (first ${data.limit} — narrow the dates)` : ''}</p>}
      <Card className="border-0 shadow-md"><CardContent className="overflow-x-auto p-0">
        {!data ? <Loader2 className="mx-auto my-12 h-6 w-6 animate-spin text-[#2D5F4F]" /> : (
          <Table data-testid="trace-table">
            <TableHeader><TableRow>{HEAD.map((h, i) => <TableHead key={h} className={`whitespace-nowrap ${i >= 4 && i <= 7 ? 'text-right' : ''}`}>{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {!data.rows.length && <TableRow><TableCell colSpan={HEAD.length} className="py-10 text-center text-gray-500" data-testid="trace-empty">No job orders in this range</TableCell></TableRow>}
              {data.rows.map((o) => <Row key={o.jo_id} o={o} onOpen={(id) => navigate(`/sections/processing/job-orders/${id}`)} />)}
            </TableBody>
          </Table>)}
      </CardContent></Card>
    </div>
  );
};

export default TraceReport;
